import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import stravaConnectButton from '../assets/strava/btn_strava_connect_with_orange.svg';
import nakasendoPreview from '../assets/images/Nakasendo_preview_cut2.png';
import './JoinMythicMapsModal.css';

const ONBOARDING_ASSET = (file) =>
  `https://firebasestorage.googleapis.com/v0/b/mythic-maps.firebasestorage.app/o/assets%2Fonboarding_modal%2F${file}?alt=media`;

const ACTIVITY_MAP_URL = ONBOARDING_ASSET('OriginalMap3D.png');

const ROTATING_MAPS = [
  { src: ONBOARDING_ASSET('StardustBrutalism3D.png'), alt: 'Stardust Brutalism Mythic Map' },
  { src: ONBOARDING_ASSET('Japanese3D.png'),          alt: 'Japanese Mythic Map' },
  { src: ONBOARDING_ASSET('PocketAdventure3D.png'),   alt: 'Pocket Adventure Mythic Map' },
  { src: ONBOARDING_ASSET('MeadowWalk3D.png'),        alt: 'Meadow Walk Mythic Map' },
];

const ROTATE_INTERVAL_MS = 3500;

const SmallStravaGlyph = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="#FC5200" aria-hidden="true">
    <path d="M15.387 17.944l-2.089-4.116h-3.065L15.387 24l5.15-10.172h-3.066m-7.008-5.599l2.836 5.598h4.172L10.463 0l-7 13.828h4.169" />
  </svg>
);

const ART_PREVIEW_URL = ONBOARDING_ASSET('Japanese3D.png');

const Chevron = ({ open }) => (
  <svg
    className={`jmm-product-chevron ${open ? 'open' : ''}`}
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
  >
    <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Asset-free before/after for the indoor fix (the hosted Zwift images are not
// uploaded yet). Conveys the promise: a ride stranded in the ocean becomes the
// real Zwift world map.
const FixWorldVisual = ({ t }) => (
  <div className="jmm-fix-visual">
    <figure className="jmm-fix-panel">
      <svg viewBox="0 0 160 110" className="jmm-fix-map" aria-hidden="true">
        <rect width="160" height="110" fill="#0a4d8c" />
        <rect width="160" height="110" fill="url(#jmmOcean)" />
        <defs>
          <linearGradient id="jmmOcean" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#1f74c4" />
            <stop offset="100%" stopColor="#0a3f73" />
          </linearGradient>
        </defs>
        <polyline points="74,52 82,44 90,54 86,66 78,64 76,56" fill="none" stroke="#fc5200" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="82" cy="56" r="20" fill="none" stroke="#fff" strokeOpacity="0.45" strokeDasharray="3 4" />
      </svg>
      <figcaption className="jmm-fix-tag">📍 {t('joinModal.products.fixBefore', 'Solomon Islands?')}</figcaption>
    </figure>
    <span className="jmm-fix-arrow" aria-hidden="true">→</span>
    <figure className="jmm-fix-panel">
      <svg viewBox="0 0 160 110" className="jmm-fix-map" aria-hidden="true">
        <rect width="160" height="110" fill="#0484e4" />
        <path d="M0,70 Q40,58 76,68 T160,62 L160,110 L0,110 Z" fill="#3a7d2c" />
        <path d="M0,82 Q48,74 96,84 T160,78 L160,110 L0,110 Z" fill="#2f6a23" />
        <polyline points="30,90 56,70 78,82 100,58 124,74 142,52" fill="none" stroke="#fc5200" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      <figcaption className="jmm-fix-tag jmm-fix-tag-after">🗺️ {t('joinModal.products.fixAfter', 'Watopia')}</figcaption>
    </figure>
  </div>
);

// The three products shown in the 'products' variant (any page that is not a
// single-product landing page). Rendered as collapsible cards so all
// three stay visible while one reveals a compelling preview at a time. One shared
// Strava CTA below covers sign-up.
const PRODUCT_CARDS = [
  { id: 'fix', icon: '🛰️', nameKey: 'joinModal.products.fixName', name: 'Fix Indoor',
    descKey: 'joinModal.products.fixDesc', desc: 'Put your indoor rides on the real Zwift world map, automatically.',
    visual: 'fix' },
  { id: 'art', icon: '🎨', nameKey: 'joinModal.products.artName', name: 'Art Maps',
    descKey: 'joinModal.products.artDesc', desc: 'Turn every Strava activity into a work of art, in 40+ styles.',
    visual: 'art' },
  { id: 'challenges', icon: '🗺️', nameKey: 'joinModal.products.challengesName', name: 'Challenges',
    descKey: 'joinModal.products.challengesDesc', desc: 'Ride real-world routes, unlock stories, and earn finisher medals.',
    visual: 'challenges' },
];

const JoinMythicMapsModal = ({ open, onDismiss, onConnect, onStravaClicked, onLearnMore, variant = 'art' }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [mapIndex, setMapIndex] = useState(0);
  const [openProduct, setOpenProduct] = useState(0);
  const rotationTimerRef = useRef(null);
  const isProducts = variant === 'products';

  useEffect(() => {
    if (open) {
      setMapIndex(0);
      setOpenProduct(0);
    }
  }, [open]);

  const renderProductVisual = (visual) => {
    if (visual === 'fix') return <FixWorldVisual t={t} />;
    if (visual === 'art') {
      return (
        <div className="jmm-product-shot">
          <img src={ART_PREVIEW_URL} alt={t('joinModal.products.artAlt', 'A Strava activity turned into a Japanese-style art map')} />
        </div>
      );
    }
    return (
      <div className="jmm-product-shot">
        <img src={nakasendoPreview} alt={t('joinModal.products.challengesAlt', 'The Nakasendo challenge route map')} />
      </div>
    );
  };

  // Auto-rotate the right-side map example, pausing when the tab is hidden.
  // Only the 'art' variant renders the rotating visual.
  useEffect(() => {
    if (!open || isProducts) return undefined;

    const startTimer = () => {
      stopTimer();
      rotationTimerRef.current = setInterval(() => {
        setMapIndex((prev) => (prev + 1) % ROTATING_MAPS.length);
      }, ROTATE_INTERVAL_MS);
    };
    const stopTimer = () => {
      if (rotationTimerRef.current) {
        clearInterval(rotationTimerRef.current);
        rotationTimerRef.current = null;
      }
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') startTimer();
      else stopTimer();
    };

    if (document.visibilityState === 'visible') startTimer();
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      stopTimer();
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [open, isProducts]);

  const handleStravaClick = useCallback(() => {
    onStravaClicked?.();
    onConnect?.();
  }, [onConnect, onStravaClicked]);

  const handleLearnMore = useCallback(() => {
    onLearnMore?.();
    onDismiss?.();
    navigate('/maps');
  }, [navigate, onDismiss, onLearnMore]);

  if (!open) return null;

  return (
    <div className="jmm-modal-overlay" onClick={onDismiss}>
      <div className="jmm-modal" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="jmm-modal-close"
          onClick={onDismiss}
          aria-label={t('joinModal.close', 'Close')}
        >
          ×
        </button>

        {isProducts ? (
          <>
            <h2 className="jmm-modal-title">
              {t('joinModal.products.title', 'Join Mythic Maps')}
            </h2>
            <p className="jmm-modal-lead">
              {t('joinModal.products.subtitle', 'One account. Three ways to ride.')}
            </p>

            <div className="jmm-modal-products">
              {PRODUCT_CARDS.map((card, i) => {
                const isOpen = openProduct === i;
                return (
                  <div className={`jmm-product-card ${isOpen ? 'open' : ''}`} key={card.id}>
                    <button
                      type="button"
                      className="jmm-product-header"
                      aria-expanded={isOpen}
                      onClick={() => setOpenProduct(isOpen ? -1 : i)}
                    >
                      <span className="jmm-product-icon" aria-hidden="true">{card.icon}</span>
                      <span className="jmm-product-name">{t(card.nameKey, card.name)}</span>
                      <Chevron open={isOpen} />
                    </button>
                    {isOpen && (
                      <div className="jmm-product-body">
                        {renderProductVisual(card.visual)}
                        <p className="jmm-product-desc">{t(card.descKey, card.desc)}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <>
            <div className="jmm-modal-visual">
              <div className="jmm-modal-visual-side jmm-modal-visual-activity">
                <img
                  src={ACTIVITY_MAP_URL}
                  alt={t('joinModal.activityAlt', 'Your Strava activity')}
                  className="jmm-modal-activity-img"
                />
                <div className="jmm-modal-visual-strava-pill">
                  <SmallStravaGlyph />
                  <span>{t('joinModal.activityLabel', 'Your activity')}</span>
                </div>
              </div>
              <div className="jmm-modal-visual-arrow" aria-hidden="true">→</div>
              <div className="jmm-modal-visual-side jmm-modal-visual-map">
                {ROTATING_MAPS.map((m, i) => (
                  <img
                    key={m.src}
                    src={m.src}
                    alt={i === mapIndex ? m.alt : ''}
                    className={`jmm-modal-map-img ${i === mapIndex ? 'visible' : ''}`}
                  />
                ))}
              </div>
            </div>

            <h2 className="jmm-modal-title">
              {t('joinModal.ctaTitle', 'Every Strava activity, becomes art.')}
            </h2>

            <ul className="jmm-modal-bullets">
              <li>
                <span className="jmm-modal-bullet-icon" aria-hidden="true">🎁</span>
                <span>{t('joinModal.ctaBullet1', 'Completely free to use.')}</span>
              </li>
              <li>
                <span className="jmm-modal-bullet-icon" aria-hidden="true">🎨</span>
                <span>{t('joinModal.ctaBullet2', '40+ artistic styles, in 2D and 3D.')}</span>
              </li>
              <li>
                <span className="jmm-modal-bullet-icon" aria-hidden="true">⚡</span>
                <span>{t('joinModal.ctaBullet3', 'Automatically get a new map for each new activity.')}</span>
              </li>
            </ul>
          </>
        )}

        <button
          type="button"
          className="jmm-modal-strava-button"
          onClick={handleStravaClick}
        >
          <img
            src={stravaConnectButton}
            alt={t('nav.connectWithStrava', 'Connect with Strava')}
          />
        </button>

        <button
          type="button"
          className="jmm-modal-learn-more"
          onClick={handleLearnMore}
        >
          {t('joinModal.learnMore', 'or learn more about Mythic Maps')}
        </button>
      </div>
    </div>
  );
};

export default JoinMythicMapsModal;
